/**
 * @file       Date format directive – auto MM/DD/YYYY with slashes
 * @version    1.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */
import { Directive, ElementRef, HostListener, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive
({
    selector: '[dateFormat]',
    standalone: false
})

/**
 * @desc   - Auto-insert '/' as user types (MM/DD/YYYY). When start date
 *           is complete, focus jumps to end date.
 */
class DateFormatDirective
{
    constructor
    (
        private el: ElementRef<HTMLInputElement>,
        @Optional() private ngControl: NgControl
    ) {}

    //__________________ P R I V A T E  M E T H O D S __________________________

    private formatDateDigits
    (
        digits: string
    ): string
    {
        let month;
        let day;
        let year;
        let monthNum;
        let dayNum;
        let result;

        digits = digits.substring(0, 8);

        month = digits.substring(0, 2);
        day = digits.substring(2, 4);
        year = digits.substring(4, 8);

        if (month.length === 2)
        {
            monthNum = Number(month);
            if (monthNum < 1 || monthNum > 12)
            {
                month = month.substring(0, 1);
                digits = month + digits.substring(2);
            }
        }

        if (day.length === 2)
        {
            dayNum = Number(day);
            if (dayNum < 1 || dayNum > 31)
            {
                day = day.substring(0, 1);
            }
        }

        result = month;
        if (digits.length > 2)
        {
            result += '/' + day;
        }
        if (digits.length > 4)
        {
            result += '/' + year;
        }

        return result;
    }

    private updateControl
    (
        formatted: string
    ): void
    {
        let dateParts;
        let month;
        let day;
        let year;
        let dateObj;

        if (!this.ngControl?.control)
        {
            return;
        }

        if (formatted.length === 0)
        {
            this.ngControl.control.setValue(null, { emitEvent: false });
        }
        else if (formatted.length === 10)
        {
            dateParts = formatted.split('/');
            month = parseInt(dateParts[0], 10);
            day = parseInt(dateParts[1], 10);
            year = parseInt(dateParts[2], 10);
            dateObj = new Date(year, month - 1, day);
            this.ngControl.control.setValue(dateObj, { emitEvent: true });
        }
        // 1–9 chars: do not setValue; keeps input display and avoids
        // control overwriting the partial value with null
    }

    private focusEndDate(): void
    {
        let rangeEl;
        let endInput;

        if (!this.el.nativeElement.hasAttribute('matStartDate'))
        {
            return;
        }

        rangeEl = this.el.nativeElement.closest('mat-date-range-input');
        if (!rangeEl)
        {
            return;
        }

        endInput = rangeEl.querySelector('input[matEndDate]') as HTMLInputElement;
        if (endInput)
        {
            setTimeout(() => endInput.focus(), 0);
        }
    }

    //___________________ P U B L I C  M E T H O D S ___________________________

    @HostListener('keydown', ['$event'])
    onKeyDown
    (
        event: KeyboardEvent
    ): void
    {
        let key;
        let input;
        let currentValue;
        let digits;
        let newDigits;
        let formatted;

        key = event.key;
        input = this.el.nativeElement;
        currentValue = input.value || '';

        if (key === 'Tab' || key === 'Escape' || key === 'Enter' ||
            key === 'ArrowLeft' || key === 'ArrowRight' ||
            key === 'ArrowUp' || key === 'ArrowDown' ||
            key === 'Home' || key === 'End' ||
            event.ctrlKey || event.metaKey)
        {
            return;
        }

        if (key === 'Backspace')
        {
            event.preventDefault();
            digits = currentValue.replace(/[^0-9]/g, '');
            if (digits.length > 0)
            {
                newDigits = digits.substring(0, digits.length - 1);
                formatted = this.formatDateDigits(newDigits);
                input.value = formatted;
                this.updateControl(formatted);
            }
            else
            {
                input.value = '';
                this.updateControl('');
            }
            return;
        }

        if (key === 'Delete')
        {
            event.preventDefault();
            input.value = '';
            this.updateControl('');
            return;
        }

        if (!/^[0-9]$/.test(key))
        {
            event.preventDefault();
            return;
        }

        event.preventDefault();

        digits = currentValue.replace(/[^0-9]/g, '');

        if (digits.length >= 8)
        {
            return;
        }

        newDigits = digits + key;
        formatted = this.formatDateDigits(newDigits);
        input.value = formatted;
        this.updateControl(formatted);

        if (formatted.length === 10)
        {
            this.focusEndDate();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste
    (
        event: ClipboardEvent
    ): void
    {
        let pastedText;
        let digits;
        let formatted;
        let input;

        event.preventDefault();

        pastedText = event.clipboardData?.getData('text') || '';
        digits = pastedText.replace(/[^0-9]/g, '').substring(0, 8);
        formatted = this.formatDateDigits(digits);

        input = this.el.nativeElement;
        input.value = formatted;
        this.updateControl(formatted);

        if (formatted.length === 10 && input.hasAttribute('matStartDate'))
        {
            this.focusEndDate();
        }
    }
}

export { DateFormatDirective };
